/**
 * Which backoffice this build is. The same codebase ships twice — the sports
 * backoffice and the clinic backoffice — against the same backend. The choice
 * is made at build time (`npm run build:clinic` sets REACT_APP_PLATFORM), so
 * nothing here changes while the app is running.
 *
 * Read the platform from here, never from process.env directly, so every
 * sports-only screen is gated through the same switch.
 */
export type PlatformKey = "SPORTS" | "CLINIC";

const raw = (process.env.REACT_APP_PLATFORM || "SPORTS").trim().toUpperCase();

export const PLATFORM: PlatformKey = raw === "CLINIC" ? "CLINIC" : "SPORTS";

export const IS_CLINIC = PLATFORM === "CLINIC";

/** The other build's address, for owners who sign in on the wrong link. */
export const OTHER_PLATFORM_URL = (
  process.env.REACT_APP_OTHER_PLATFORM_URL || ""
).trim();

export const PLATFORM_NAMES: Record<PlatformKey, string> = {
  SPORTS: "Sports",
  // The self-serve product: clinics, spas, salons and the rest.
  CLINIC: "Appointments",
};

/**
 * The kind of business a self-serve organization runs. Everyone signs up on
 * the same link; the type then decides the words, colours and starting lists
 * the app shows. Owners can change it in Settings. Mirrors the backend's
 * `business_type` enum.
 */
export type BusinessTypeKey = "NEUTRAL" | "CLINIC" | "SPA" | "SALON" | "BEAUTY";

export const BUSINESS_TYPE_OPTIONS: {
  key: BusinessTypeKey;
  name: string;
  description: string;
}[] = [
  {
    key: "CLINIC",
    name: "Clinic",
    description: "Doctors, dentists, physiotherapy",
  },
  {
    key: "SPA",
    name: "Spa & wellness",
    description: "Massage, treatments, wellness",
  },
  {
    key: "SALON",
    name: "Hair salon & barber",
    description: "Haircuts, colour, beard",
  },
  {
    key: "BEAUTY",
    name: "Beauty",
    description: "Nails, lashes, brows, makeup",
  },
  {
    key: "NEUTRAL",
    name: "Something else",
    description: "Any business that takes appointments",
  },
];

const isBusinessType = (value: unknown): value is BusinessTypeKey =>
  BUSINESS_TYPE_OPTIONS.some((option) => option.key === value);

// Read once, from the signed-in user saved at login, before anything renders:
// the theme and the words below are built from it when the app starts, so a
// change of type reloads the page (see `applyBusinessType`).
const storedBusinessType = (): BusinessTypeKey => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user) return "NEUTRAL";
    if (isBusinessType(user.businessType)) return user.businessType;
    // Signed in before the type existed: every one of those is a clinic.
    return "businessType" in user ? "NEUTRAL" : "CLINIC";
  } catch {
    return "NEUTRAL";
  }
};

/** This organization's type; always NEUTRAL in the sports build and when signed out. */
export const BUSINESS_TYPE: BusinessTypeKey = IS_CLINIC
  ? storedBusinessType()
  : "NEUTRAL";

/**
 * Saves a new type on the signed-in user and reloads into `path` when the
 * app has to repaint for it. Returns true when a reload was started.
 */
export const applyBusinessType = (type: unknown, path?: string): boolean => {
  if (!IS_CLINIC) return false;
  const next = isBusinessType(type) ? type : "NEUTRAL";
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user)
      localStorage.setItem(
        "user",
        JSON.stringify({ ...user, businessType: next }),
      );
  } catch {
    // Storage unavailable: the app keeps its current look.
  }
  if (next === BUSINESS_TYPE) return false;
  window.location.assign(
    path ?? window.location.pathname + window.location.search,
  );
  return true;
};

/** Colours of each business type (white text on `main` clears 4.5:1). */
export const BUSINESS_THEMES: Record<
  BusinessTypeKey,
  {
    main: string;
    tint: string;
    background: string;
    border: string;
    text: string;
    textSecondary: string;
    secondary: string;
  }
> = {
  NEUTRAL: {
    main: "#4F46E5",
    tint: "#EEF2FF",
    background: "#F7F7FB",
    border: "#E0E1EC",
    text: "#1E1B4B",
    textSecondary: "#4B5068",
    secondary: "#0F766E",
  },
  CLINIC: {
    main: "#0E7490",
    tint: "#E6F6F9",
    background: "#F5F9FA",
    border: "#DCE5E8",
    text: "#12343B",
    textSecondary: "#4B6066",
    secondary: "#15803D",
  },
  SPA: {
    main: "#2F6F5E",
    tint: "#E7F2EE",
    background: "#F6FAF8",
    border: "#DAE6E1",
    text: "#1D3A32",
    textSecondary: "#4E6660",
    secondary: "#A16207",
  },
  SALON: {
    main: "#9A3412",
    tint: "#FBEDE6",
    background: "#FAF7F5",
    border: "#ECDFD8",
    text: "#3B2418",
    textSecondary: "#6B5248",
    secondary: "#334155",
  },
  BEAUTY: {
    main: "#BE185D",
    tint: "#FCE9F1",
    background: "#FBF7F9",
    border: "#F0DDE6",
    text: "#3F1A2B",
    textSecondary: "#6E4F5D",
    secondary: "#7C3AED",
  },
};

export const BUSINESS_THEME = BUSINESS_THEMES[BUSINESS_TYPE];

const SELF_SERVE_BRAND: Record<
  BusinessTypeKey,
  { name: string; productName: string; tagline: string }
> = {
  NEUTRAL: {
    name: "Softwarezz Booking",
    productName: "Appointments",
    tagline: "Appointments, clients and payments for your business.",
  },
  CLINIC: {
    name: "Softwarezz Clinic",
    productName: "Clinic Booking",
    tagline: "Appointments, patients and payments for your clinic.",
  },
  SPA: {
    name: "Softwarezz Spa",
    productName: "Spa Booking",
    tagline: "Treatments, clients and payments for your spa.",
  },
  SALON: {
    name: "Softwarezz Salon",
    productName: "Salon Booking",
    tagline: "Appointments, clients and payments for your salon.",
  },
  BEAUTY: {
    name: "Softwarezz Beauty",
    productName: "Beauty Booking",
    tagline: "Appointments, clients and payments for your studio.",
  },
};

export const BRAND = IS_CLINIC
  ? SELF_SERVE_BRAND[BUSINESS_TYPE]
  : {
      name: "Softwarezz",
      productName: "Booking System",
      tagline: "Run your venue: bookings, clients and payments.",
    };

/**
 * Words that differ between the two products. Only the user-facing copy —
 * API fields keep their names (a clinic "room" is still a `unit`).
 */
type Labels = {
  venue: string;
  venues: string;
  unit: string;
  units: string;
  allUnits: string;
  client: string;
  clients: string;
  booking: string;
  bookings: string;
  clientFile: string;
  medicalInfo: string;
  visit: string;
  visits: string;
  /** Who a calendar belongs to, for help texts ("doctor", "stylist"). */
  staff: string;
  staffPlural: string;
  /** An example calendar name. */
  unitExample: string;
};

// Each column is whatever the owner names it (Dr. Ahmad, Room 2, Karim), so
// the generic word stays "Calendar" in every type.
const calendar = {
  unit: "Calendar",
  units: "Calendars",
  allUnits: "All calendars",
};

const SELF_SERVE_LABELS: Record<BusinessTypeKey, Labels> = {
  NEUTRAL: {
    venue: "Business",
    venues: "Businesses",
    ...calendar,
    client: "Client",
    clients: "Clients",
    booking: "Appointment",
    bookings: "Appointments",
    clientFile: "Client file",
    medicalInfo: "Health info",
    visit: "Appointment",
    visits: "Appointments",
    staff: "team member",
    staffPlural: "team",
    unitExample: "Sara",
  },
  CLINIC: {
    venue: "Clinic",
    venues: "Clinics",
    ...calendar,
    client: "Patient",
    clients: "Patients",
    booking: "Appointment",
    bookings: "Appointments",
    clientFile: "Patient file",
    medicalInfo: "Medical info",
    visit: "Visit",
    visits: "Visits",
    staff: "doctor",
    staffPlural: "doctors",
    unitExample: "Dr. Ahmad",
  },
  SPA: {
    venue: "Spa",
    venues: "Spas",
    ...calendar,
    client: "Client",
    clients: "Clients",
    booking: "Appointment",
    bookings: "Appointments",
    clientFile: "Client file",
    medicalInfo: "Health & allergies",
    visit: "Treatment",
    visits: "Treatments",
    staff: "therapist",
    staffPlural: "therapists",
    unitExample: "Maya",
  },
  SALON: {
    venue: "Salon",
    venues: "Salons",
    ...calendar,
    client: "Client",
    clients: "Clients",
    booking: "Appointment",
    bookings: "Appointments",
    clientFile: "Client card",
    medicalInfo: "Allergies & notes",
    visit: "Appointment",
    visits: "Appointments",
    staff: "stylist",
    staffPlural: "stylists",
    unitExample: "Karim",
  },
  BEAUTY: {
    venue: "Studio",
    venues: "Studios",
    ...calendar,
    client: "Client",
    clients: "Clients",
    booking: "Appointment",
    bookings: "Appointments",
    clientFile: "Client card",
    medicalInfo: "Allergies & sensitivities",
    visit: "Appointment",
    visits: "Appointments",
    staff: "artist",
    staffPlural: "artists",
    unitExample: "Lara",
  },
};

/**
 * Words that differ between the products. Only the user-facing copy — API
 * fields keep their names (a clinic "room" is still a `unit`).
 */
export const LABELS: Labels = IS_CLINIC
  ? SELF_SERVE_LABELS[BUSINESS_TYPE]
  : {
      venue: "Venue",
      venues: "Venues",
      unit: "Court",
      units: "Courts",
      allUnits: "All courts",
      client: "Client",
      clients: "Clients",
      booking: "Booking",
      bookings: "Bookings",
      clientFile: "Client file",
      medicalInfo: "Health info",
      visit: "Booking",
      visits: "Bookings",
      staff: "court",
      staffPlural: "courts",
      unitExample: "Court 1",
    };

/** Sports-only parts of the backoffice, hidden in the clinic build. */
export const SHOW = {
  matchmaking: !IS_CLINIC,
  publicMatch: !IS_CLINIC,
  playerLevels: !IS_CLINIC,
  /** Loyalty points split across the players of a match. */
  playersPerMatch: !IS_CLINIC,
  sportcietyColumn: !IS_CLINIC,
  /**
   * Coaches, Classes and session Packages. Package sessions are only spent by
   * class attendance, so a clinic selling them for regular appointments would
   * never see a session deducted. Hidden until packages can be redeemed
   * against normal appointments.
   */
  classes: !IS_CLINIC,
  /**
   * Doctors & Staff and Specialities pages. Off in both builds for now: the
   * Staff page calls POST /staff/all (the backend serves GET) and the backend
   * has no specialities endpoints, so both are leftovers from the old clinic
   * app. Flip on once they are wired to this backend.
   */
  staffPage: false,
  specialities: false,
};
